// Single app-wide signal fired when the host environment thinks the
// network may have come back: mobile app returned from background, OS
// reported a network reconnect, browser fired `online`. Active-call
// hooks (`useConnectionReconnect`) and the GraphQL client subscribe to
// it and re-run their recovery logic without each one having to wire
// up a separate listener.
const RECOVER_EVENT = "app:network-recover";

export function triggerNetworkRecovery(reason: string): void {
  // eslint-disable-next-line no-console
  console.info("[network-recovery] triggered:", reason);
  window.dispatchEvent(new CustomEvent(RECOVER_EVENT, { detail: { reason } }));
}

// Mobile resume routinely fires multiple recovery signals back-to-back
// (Capacitor App.appStateChange + Network plugin's networkStatusChange,
// both in the same tick, plus the synthetic `online` event we fire to
// nudge Apollo). Without coalescing, every active call hook + the
// GraphQL link would each kick off an independent recovery, multiplying
// load on the backend at exactly the moment the user's network is its
// shakiest. We debounce per-subscriber so each handler sees one trailing
// invocation with the most recent reason.
const DEBOUNCE_MS = 250;

export function onNetworkRecovery(handler: (reason: string) => void): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let latestReason = "unknown";
  const wrapped = (e: Event) => {
    latestReason = (e as CustomEvent<{ reason: string }>).detail?.reason || "unknown";
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      const r = latestReason;
      latestReason = "unknown";
      handler(r);
    }, DEBOUNCE_MS);
  };
  window.addEventListener(RECOVER_EVENT, wrapped);
  return () => {
    if (timer) clearTimeout(timer);
    window.removeEventListener(RECOVER_EVENT, wrapped);
  };
}
